import { getCart, updateQuantity, removeLine, type CartLine } from './cartStore.ts';

export interface CartLineResult {
  productId: string;
  colorId: string;
  sizeEu: string;
  quantity: number;
  name: string;
  slug: string;
  imageUrl: string;
  unitPrice: number;
  availableStock: number;
  isAvailable: boolean;
}

export interface CartLinesView {
  linesEl: HTMLElement;
  emptyEl: HTMLElement;
  contentEl: HTMLElement;
  totalEl: HTMLElement;
}

interface MountState {
  lines: CartLineResult[];
  render: () => void;
}

const stateByElement = new WeakMap<HTMLElement, MountState>();

export function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

function sameLine(a: CartLineResult, b: Pick<CartLine, 'productId' | 'colorId' | 'sizeEu'>): boolean {
  return a.productId === b.productId && a.colorId === b.colorId && a.sizeEu === b.sizeEu;
}

export async function fetchLines(cart: CartLine[]): Promise<CartLineResult[]> {
  const query = encodeURIComponent(JSON.stringify(cart));
  const res = await fetch(`/api/panier/lignes?lignes=${query}`);
  const body = await res.json();
  return body.data ?? [];
}

function renderLineHtml(line: CartLineResult): string {
  const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

  if (!line.isAvailable) {
    return `
      <li class="panier__line panier__line--unavailable" data-product-id="${escapeAttr(line.productId)}" data-color-id="${escapeAttr(line.colorId)}" data-size-eu="${escapeAttr(line.sizeEu)}">
        <div class="panier__line-details">
          <div class="panier__line-info">
            <span class="panier__line-name">${line.name || 'Produit indisponible'}</span>
            <div class="panier__line-unavailable-msg">Ce produit n'est plus disponible.</div>
          </div>
          <button type="button" class="panier__line-remove">Retirer</button>
        </div>
      </li>`;
  }

  return `
    <li class="panier__line" data-product-id="${escapeAttr(line.productId)}" data-color-id="${escapeAttr(line.colorId)}" data-size-eu="${escapeAttr(line.sizeEu)}" data-stock="${line.availableStock}">
      ${line.imageUrl ? `<img class="panier__line-image" src="${escapeAttr(line.imageUrl)}" alt="${escapeAttr(line.name)}">` : ''}
      <div class="panier__line-details">
        <div class="panier__line-info">
          <a class="panier__line-name" href="/produit/${escapeAttr(line.slug)}">${line.name}</a>
          <div class="panier__line-meta">Taille EU ${line.sizeEu}</div>
        </div>
        <div class="panier__line-quantity">
          <button type="button" class="panier__qty-btn panier__qty-btn--minus" aria-label="Diminuer la quantité">−</button>
          <span class="panier__qty-value">${line.quantity}</span>
          <button type="button" class="panier__qty-btn panier__qty-btn--plus" aria-label="Augmenter la quantité">+</button>
        </div>
        <div class="panier__line-price">${formatPrice(line.unitPrice * line.quantity)}</div>
        <button type="button" class="panier__line-remove">Retirer</button>
      </div>
    </li>`;
}

/** Affiche les lignes du panier dans `view` (page `/panier` ou sidebar mini-cart) —
 * peut être appelée plusieurs fois sur les mêmes éléments (ex : à chaque ouverture
 * de la sidebar) : le câblage des clics n'est fait qu'une fois par élément
 * (`linesEl`), l'état courant est ensuite lu depuis `stateByElement` à chaque clic. */
export async function mountCartLines(view: CartLinesView): Promise<void> {
  const cart = getCart();
  if (cart.length === 0) {
    view.emptyEl.hidden = false;
    view.contentEl.hidden = true;
    return;
  }

  const fetchedLines = await fetchLines(cart);
  if (fetchedLines.length === 0) {
    view.emptyEl.hidden = false;
    view.contentEl.hidden = true;
    return;
  }

  view.emptyEl.hidden = true;
  view.contentEl.hidden = false;

  function render(): void {
    const state = stateByElement.get(view.linesEl)!;
    view.linesEl.innerHTML = state.lines.map(renderLineHtml).join('');
    const total = state.lines.filter((l) => l.isAvailable).reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
    view.totalEl.textContent = formatPrice(total);
  }

  stateByElement.set(view.linesEl, { lines: fetchedLines, render });

  if (!view.linesEl.dataset.cartLinesWired) {
    view.linesEl.dataset.cartLinesWired = 'true';

    view.linesEl.addEventListener('click', (e) => {
      const state = stateByElement.get(view.linesEl);
      if (!state) return;

      const target = e.target as HTMLElement;
      const li = target.closest<HTMLElement>('.panier__line');
      if (!li) return;

      const productId = li.dataset.productId!;
      const colorId = li.dataset.colorId!;
      const sizeEu = li.dataset.sizeEu!;
      const line = state.lines.find((l) => sameLine(l, { productId, colorId, sizeEu }));
      if (!line) return;

      if (target.closest('.panier__line-remove')) {
        removeLine(productId, colorId, sizeEu);
        state.lines = state.lines.filter((l) => l !== line);
        if (state.lines.length === 0) {
          view.contentEl.hidden = true;
          view.emptyEl.hidden = false;
          return;
        }
        state.render();
        return;
      }

      if (target.closest('.panier__qty-btn--minus')) {
        line.quantity = Math.max(1, line.quantity - 1);
        updateQuantity(productId, colorId, sizeEu, line.quantity);
        state.render();
        return;
      }

      if (target.closest('.panier__qty-btn--plus')) {
        line.quantity = Math.min(line.availableStock, line.quantity + 1);
        updateQuantity(productId, colorId, sizeEu, line.quantity);
        state.render();
        return;
      }
    });
  }

  render();
}
